/**
 * Generate all possible part combinations with different orientations
 * @param {Array} allParts - Array of all available parts
 * @param {number} selectionSize - Number of parts to select in each combination (e.g., 2)
 * @returns {Array} Array of all possible part combinations with orientations
 */
export function generatePartSelectionCombinations(allParts, selectionSize) {
  const combinations = [];
  let combinationId = 1;

  // Helper function to generate combinations recursively
  function generatePartCombinations(
    startIndex,
    currentCombination,
    selectionSize
  ) {
    // If we've selected enough parts
    if (currentCombination.length === selectionSize) {
      // For each part combination, generate all orientation combinations
      const orientationCombinations = generateOrientationVariants(
        currentCombination,
        selectionSize
      );
      combinations.push(
        ...orientationCombinations.map((combo) => {
          return {
            id: combinationId++,
            partSelections: currentCombination.map((p) => p.id),
            selectionSize: selectionSize,
            ...combo,
          };
        })
      );
      return;
    }

    // Recursively select parts
    for (let i = startIndex; i < allParts.length; i++) {
      generatePartCombinations(
        i + 1,
        [...currentCombination, allParts[i]],
        selectionSize
      );
    }
  }

  // Start the recursive generation with empty selection
  generatePartCombinations(0, [], selectionSize);
  // loop for each combination
  for (let i = 0; i < combinations.length; i++) {
    const combo = combinations[i];
    // check if combo.desription constains 'both orientations'
    if (
      combo.description.includes('both orientations') &&
      combo.parts.length > 2
    ) {
      // remove combo from combinations
      combinations.splice(i, 1);
      i--; // Adjust index after removal
      continue; // Skip to the next iteration
    }
  }
  return combinations;
}

/**
 * Generate all orientation variants for a specific set of parts
 * @param {Array} selectedParts - The parts to generate orientation variants for
 * @returns {Array} All orientation combinations for these parts
 */
export function generateOrientationVariants(selectedParts, selectionSize) {
  const numParts = selectedParts.length;

  // For 3 orientations (landscape, portrait, both), we need 3^n combinations
  const totalCombinations = Math.pow(3, numParts);
  const orientationCombinations = [];

  // Generate each orientation combination
  for (let i = 0; i < totalCombinations; i++) {
    const combination = {
      description: `Test with `,
      parts: [],
    };

    // Convert number to ternary (base-3) representation
    // 0 = landscape, 1 = portrait, 2 = both orientations
    let remainingValue = i;
    const orientationValues = [];

    for (let j = 0; j < numParts; j++) {
      const value = remainingValue % 3;
      remainingValue = Math.floor(remainingValue / 3);
      orientationValues.unshift(value);
    }

    const orientationDescriptions = [];

    // Create combinations based on ternary values
    for (let j = 0; j < numParts; j++) {
      const part = { ...selectedParts[j] };
      const orientationValue = orientationValues[j];

      // Handle the three orientation types
      if (orientationValue === 0) {
        // Landscape
        orientationDescriptions.push(`${part.id} in landscape`);
        combination.parts.push({
          ...part,
          orientation: 'landscape',
        });
      } else if (orientationValue === 1) {
        // Portrait
        orientationDescriptions.push(`${part.id} in portrait`);
        combination.parts.push({
          ...part,
          width: part.height,
          height: part.width,
          orientation: 'portrait',
        });
      } else {
        orientationDescriptions.push(`${part.id} in both orientations`);

        // Add landscape version
        combination.parts.push({
          ...part,
          orientation: 'landscape',
          id: `${part.id}-landscape`, // Create unique ID for this orientation
        });

        // Add portrait version
        combination.parts.push({
          ...part,
          width: part.height,
          height: part.width,
          orientation: 'portrait',
          id: `${part.id}-portrait`, // Create unique ID for this orientation
        });
        // }
      }
    }

    combination.description += orientationDescriptions.join(', ');
    orientationCombinations.push(combination);
  }

  return orientationCombinations;
}

export function generateAllPartCombinations(parts) {
  let partLength = parts.length;
  let hasil = [];
  for (let i = 0; i < partLength; i++) {
    const combination = generatePartSelectionCombinations(parts, i + 1);
    hasil.push({
      partCount: i + 1,
      combinations: combination,
    });
  }
  return hasil;
}
